import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface DraftData {
  title: string;
  titleZh?: string;
  content: string;
  contentZh?: string;
  tags: string;
  visibility: 'public' | 'private' | 'unlisted';
  slug: string;
  updatedAt: number;
}

interface DraftState {
  drafts: Record<string, DraftData>;
  saveDraft: (articleId: string, data: Partial<DraftData>) => void;
  getDraft: (articleId: string) => DraftData | null;
  deleteDraft: (articleId: string) => void;
  clearOldDrafts: () => void;
}

const DRAFT_EXPIRY_DAYS = 30;

export const useDraftStore = create<DraftState>()(
  persist(
    (set, get) => ({
      drafts: {},

      saveDraft: (articleId: string, data: Partial<DraftData>) => {
        const existingDraft = get().drafts[articleId] || {
          title: '',
          content: '',
          tags: '',
          visibility: 'public' as const,
          slug: '',
        };

        set((state) => ({
          drafts: {
            ...state.drafts,
            [articleId]: {
              ...existingDraft,
              ...data,
              updatedAt: Date.now(),
            },
          },
        }));
      },

      getDraft: (articleId: string) => {
        const draft = get().drafts[articleId];
        if (!draft) return null;

        // Check if draft is expired
        const expiryTime = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        if (Date.now() - draft.updatedAt > expiryTime) {
          get().deleteDraft(articleId);
          return null;
        }

        return draft;
      },

      deleteDraft: (articleId: string) => {
        set((state) => {
          const { [articleId]: _, ...rest } = state.drafts;
          return { drafts: rest };
        });
      },

      clearOldDrafts: () => {
        const expiryTime = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        const now = Date.now();

        set((state) => {
          const validDrafts: Record<string, DraftData> = {};
          for (const [id, draft] of Object.entries(state.drafts)) {
            if (now - draft.updatedAt < expiryTime) {
              validDrafts[id] = draft;
            }
          }
          return { drafts: validDrafts };
        });
      },
    }),
    {
      name: 'draft-storage',
    }
  )
);
